<script setup lang="ts">
import { ClipboardCheckIcon, ClipboardCopyIcon } from '@heroicons/vue/outline';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from '#imports';
import AddressActivity from '~/components/address/AddressActivity.vue';
import { getApiRoot } from '~/utilities/api';
import { BASE_TITLE } from '~/utilities/constants';

const route = useRoute();
const router = useRouter();

let id = route.params?.id;
if (id && id.includes('-')) {
  id = id.split('-').pop();
}

const { data: address } = await useFetch<any>(`${getApiRoot()}/address/${id}?include_counts=True`);
const name = computed(() => address.value?.encoded_id.substring(0, 24));

const meta = [
  { property: 'og:title', content: `${name.value || 'Unknown'} - Address ${BASE_TITLE}` },
  { name: 'twitter:title', content: `${name.value || 'Unknown'} - Address ${BASE_TITLE}` },
];

useHead({
  title: `${name.value || 'Unknown'} - Address ${BASE_TITLE}`,
  meta,
} as any);

const tabs = computed(() => {
  return [
    // {
    //   id: 'created',
    //   title: 'profile.created',
    //   count: address.value?.created_nfts_count || 0,
    // },
    {
      id: 'owned',
      title: 'address.owned',
      count: address.value?.owned_nfts_count || 0,
    },
    {
      id: 'activity',
      title: 'address.activity',
    },
  ];
});

// const defaultTab = address.value?.owned_nfts_count > 0 && address.value?.created_nfts_count === 0 ? 'owned' : 'created';
const defaultTab = 'owned';
const selectedTab = ref(route.query.tab ? route.query.tab : defaultTab);
watch(selectedTab, (tab) => {
  router.replace({
    path: route.path,
    query: { tab },
  });
});

const copied = ref(false);
const copyToClipboard = (input: string) => {
  navigator.clipboard.writeText(input);
  copied.value = true;
  setTimeout(() => (copied.value = false), 1000);
};

const gridStyle = useCookie<'grid-compact' | 'grid' | 'table'>('grid-style');
gridStyle.value = gridStyle.value || 'grid';
</script>

<template>
  <div class="flex flex-grow flex-col">
    <Error404 v-if="!address" />
    <template v-else>
      <div class="flex flex-col">
        <div class="mt-8 grid grid-rows-[auto_auto_auto]">
          <Container
            class="col-start-1 row-start-3 mt-8 flex flex-col justify-center gap-4 md:flex-row md:justify-between"
          >
            <div class="flex flex-col items-center gap-4 md:items-start">
              <h1 class="flex items-center gap-1 text-xl font-bold text-neutral-900 dark:text-neutral-50 md:text-3xl">
                {{ $t('address.address') }}
              </h1>
              <div class="flex flex-col flex-wrap items-center justify-center gap-4 md:flex-row">
                <div class="flex">
                  <div
                    class="shadow-card group flex cursor-pointer items-center justify-center overflow-hidden rounded-full border pr-2 text-sm font-medium transition duration-200 ease-in-out"
                    @click="copyToClipboard(address.encoded_id)"
                  >
                    <!--                    <div class="rounded-full bg-emerald-700 px-3 py-2 font-mono tracking-tight text-white dark:text-black">xch</div>-->
                    <span class="py-2 pl-3 font-mono tracking-tight text-neutral-500 dark:text-neutral-400">{{
                      address.encoded_id
                    }}</span>
                    <div class="mb-[1px] ml-1 inline flex">
                      <ClipboardCheckIcon v-if="copied" class="h-6 w-6 text-emerald-600" />
                      <ClipboardCopyIcon
                        v-else
                        class="ml-[1px] h-6 w-6 text-neutral-300 group-hover:text-neutral-600 dark:text-neutral-300 dark:text-neutral-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="address.associated_profiles?.length > 0" class="flex flex-col gap-2">
                <div class="flex items-center text-sm font-bold text-neutral-700 dark:text-neutral-200">
                  Associated Profiles<span
                    class="ml-1 inline-flex items-center rounded bg-gradient-to-br from-cyan-500 to-emerald-500 px-1 text-[10px] font-bold text-white dark:text-black"
                    >BETA</span
                  >
                </div>
                <div class="flex gap-4">
                  <div v-for="profile in address.associated_profiles" :key="profile.id">
                    <ProfileHandle :profile="profile" />
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
      <div class="w-full flex-grow bg-white pt-8 dark:bg-black">
        <Container>
          <div>
            <div>
              <Tabs v-model="selectedTab" :tabs="tabs">
                <div v-if="selectedTab !== 'collections'" class="flex h-full items-center px-3">
                  <ExploreGridToggle v-model="gridStyle" />
                </div>
              </Tabs>
            </div>
            <div class="py-8">
              <AddressNfts
                v-if="selectedTab === 'created'"
                :address="address"
                :type="selectedTab"
                :grid-style="gridStyle"
              />
              <AddressNfts
                v-else-if="selectedTab === 'owned'"
                :address="address"
                :type="selectedTab"
                :grid-style="gridStyle"
              />
              <AddressActivity v-else-if="selectedTab === 'activity'" :address="address" />
            </div>
          </div>
        </Container>
      </div>
    </template>
  </div>
</template>
